import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import {LABELS} from '../constants'
import {useSavedViewEndpoints} from '../contexts/SavedViewEndpointsContext'
import type {CreateSavedViewResponse, DuplicateSavedViewVariables} from '../types'
import {
  primeSavedViewCaches,
  type SavedViewMutationOptions,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'

export function useDuplicateSavedViewMutation<TConfiguration = never>({owner, repo}: SavedViewMutationOptions) {
  const queryClient = useQueryClient()
  const {basePath} = useSavedViewEndpoints(owner, repo)

  return useMutation<CreateSavedViewResponse<TConfiguration>, Error, DuplicateSavedViewVariables<TConfiguration>>({
    mutationFn: ({view, name, configuration}) =>
      sendSavedViewMutationJson<CreateSavedViewResponse<TConfiguration>>(
        `${basePath}.json`,
        {
          method: 'POST',
          body: {
            name,
            query: view.query,
            description: view.description ?? undefined,
            icon: view.icon,
            color: view.color,
            source: 'duplicate',
            visibility: view.visibility,
            ...(configuration !== undefined ? {configuration} : {}),
          },
        },
        LABELS.views.duplicateError,
      ),
    onSuccess: ({savedView}) => primeSavedViewCaches({queryClient, owner, repo}, savedView),
    onError: () => announce(LABELS.views.duplicateError, {assertive: true}),
  })
}
